import React, { useState } from 'react';
import Button from '../Button';
import ConfirmDialog from '../ConfirmDialog';
import Icon from '../Icon';
import CustomTemplateDrawer from './CustomTemplateDrawer';
import { useMessageTemplatesIfProvided } from '../../data/useMessageTemplates';
import { composeWord } from '../../domain/compose';
import s from './settings.module.css';

// Business Settings → Email & SMS → Custom Email Templates (#608).
//
// MMS's: below System Email Templates and System SMS Templates, the heading,
// the sentence under it and `+ Add New`; each saved template a row by name with
// an edit and a delete, the delete asking first ("Delete Custom Template",
// #605); with none saved, no line saying there are none. They are New
// Message's `Templates ▾` list (`messageTemplates`, #597): one saved from the
// composer is here, and one deleted here is gone from the menu, which has no
// Delete of its own, as MMS's has none.
//
// Ours does not have MMS's two System template lists (MessagingPanel.jsx
// says why), so this section follows the sending identity.
//
// The list is the provider's; a panel rendered without it (a test of the
// rest of the panel) draws no section rather than an empty one.
export default function CustomEmailTemplates() {
  const templates = useMessageTemplatesIfProvided();
  // `{ template }`: null for Add New, else the one being edited.
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  if (!templates) return null;

  const remove = () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) templates.removeTemplate(id);
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <div className={s.panelTitle}>{composeWord('custom_heading')}</div>
          <p className={s.blurb}>{composeWord('custom_blurb')}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setEditing({ template: null })}>
          + {composeWord('custom_add')}
        </Button>
      </div>
      {templates.templates.length ? (
        <ul className={s.templateRows} aria-label={composeWord('custom_heading')}>
          {templates.templates.map((t) => (
            <li key={t.id} className={s.templateRow}>
              <span className={s.templateName}>{t.label}</span>
              <button
                type="button"
                className={s.templateAction}
                aria-label={`${composeWord('custom_edit')} ${t.label}`}
                title={composeWord('custom_edit')}
                onClick={() => setEditing({ template: t })}
              >
                <Icon name="pencil" />
              </button>
              <button
                type="button"
                className={s.templateAction}
                aria-label={`${composeWord('custom_delete')} ${t.label}`}
                title={composeWord('custom_delete')}
                onClick={() => setDeleting(t)}
              >
                <Icon name="trash" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {editing ? (
        <CustomTemplateDrawer
          key={editing.template?.id || 'new'}
          template={editing.template}
          templates={templates}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(deleting)}
        title={composeWord('custom_delete_title')}
        message={composeWord('custom_delete_text')}
        discardLabel={composeWord('custom_delete')}
        cancelLabel={composeWord('custom_cancel')}
        onDiscard={remove}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
